// src/App.jsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "next-themes";
import { Toaster } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import store from "./store/store";

// Layouts & Pages
import MainLayout from "./components/layout/MainLayout";
import LoginPage from "./pages/auth/LoginPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import SecurityQuestionLoginPage from "./pages/auth/SecurityQuestionLoginPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import SecuritySetupPage from "./pages/auth/SecuritySetupPage";
import Dashboard from "./pages/Dashboard";
import EmployeePage from "./pages/modules/EmployeePage";
import RolePage from "./pages/modules/RolePage";
import DepartmentPage from "./pages/modules/DepartmentPage";
import SecurityEmployeeQuestionPage from "./pages/modules/SecurityEmployeeQuestionPage";
import CategoryPage from "./pages/modules/CategoryPage";
import SubcategoryPage from "./pages/modules/SubcategoryPage";
import InventoryPage from "./pages/modules/InventoryPage";
import StockPage from "./pages/modules/StockPage";
import CanteenPage from "./pages/modules/CanteenPage";
import GeneralLogPage from "./pages/modules/GeneralLogPage";
import InventoryLogPage from "./pages/modules/InventoryLogPage";
import ProfilePage from "./pages/modules/ProfilePage";
import RaiseTicketPage from "./pages/modules/RaiseTicketPage";
import RequestIssuancePage from "./pages/modules/RequestIssuancePage";
import NotificationsPage from "./pages/modules/NotificationsPage";
import AssetAssignPage from "./pages/modules/AssetAssignPage";

// HOOKS
import usePermissionSocket from "./hooks/usePermissionSocket";
import { usePermissions } from "./hooks/usePermissions";
import SuggestionPage from "./pages/modules/SuggestionPage";
import BreakTimeLogPage from "./pages/modules/BreakTimeLogPage";
import RequestLogsPage from "./pages/modules/RequestLogsPage";
import AssetAssignLogsPage from "./pages/modules/AssetAssignLogsPage";
import TicketLogsPage from "./pages/modules/TicketLogsPage";
import UpdateWalletPage from "./pages/modules/UpdateWalletPage";
import CanteenLogsPage from "./pages/modules/CanteenLogsPage";
import OrderListPage from "./pages/modules/OrderListPage";
import CustomerProfilePage from "./pages/CustomerProfilePage";
import SettingsPage from "./pages/modules/SettingsPage";
import FeedbackPage from "./pages/modules/FeedbackPage";
import ReportPage from "./pages/modules/ReportPage";
import { Loader2 } from "lucide-react";
import EscalationHistoryLogPage from "./pages/modules/EscalationHistoryLogPage";
import EmployeeLogPage from "./pages/modules/EmployeeLogPage";
import PermissionsLogPage from "./pages/modules/PermissionsLogPage";
import BreakLogPage from "./pages/modules/BreakLogPage";
import MyTeamPage from "./pages/modules/MyTeamPage";
import CompanyPage from "./pages/modules/CompanyPage";
import VendorPage from "./pages/modules/VendorPage";
import InventorySummaryPage from "./pages/reports/InventorySummaryPage";
import StockMovementPage from "./pages/reports/StockMovementPage";
import LowStockAlertPage from "./pages/reports/LowStockAlertPage";
import ExpiredItemsPage from "./pages/reports/ExpiredItemsPage";
import ConsumptionTrendPage from "./pages/reports/ConsumptionTrendPage";
import DepartmentConsumptionPage from "./pages/reports/DepartmentConsumptionPage";
import PurchaseHistoryPage from "./pages/reports/PurchaseHistoryPage";
import PriceVariationPage from "./pages/reports/PriceVariationPage";
import AssetMasterPage from "./pages/reports/AssetMasterPage";
import WarrantyExpiryPage from "./pages/reports/WarrantyExpiryPage";
import AssetAllocationPage from "./pages/reports/AssetAllocationPage";
import IdleAssetsPage from "./pages/reports/IdleAssetsPage";
import AssetLifecycleCostPage from "./pages/reports/AssetLifecycleCostPage";
import BreakSummaryPage from "./pages/reports/BreakSummaryPage";
import BreakDurationPage from "./pages/reports/BreakDurationPage";
import IrregularBreaksPage from "./pages/reports/IrregularBreaksPage";
import TimeDeviationPage from "./pages/reports/TimeDeviationPage";
import TicketsOverviewPage from "./pages/reports/TicketsOverviewPage";
import TatCompliancePage from "./pages/reports/TatCompliancePage";
import EscalationsPage from "./pages/reports/EscalationsPage";
import DepartmentTicketsPage from "./pages/reports/DepartmentTicketsPage";
import RecurringIssuesPage from "./pages/reports/RecurringIssuesPage";
import HandlerProductivityPage from "./pages/reports/HandlerProductivityPage";
import CanteenOrdersPage from "./pages/reports/CanteenOrdersPage";
import PopularItemsPage from "./pages/reports/PopularItemsPage";
import CanteenFinancePage from "./pages/reports/CanteenFinancePage";
import IngredientStockPage from "./pages/reports/IngredientStockPage";
import ReportLayout from "./components/layout/ReportLayout";
import AttendancePage from "./pages/modules/AttendancePage";

// --- SECURITY LOCK: BLOCK BACK + URL TAMPERING ---
// const useSecurityLock = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const isFirstLogin = localStorage.getItem("isFirstLogin") === "true";
//   const prevPathnameRef = React.useRef("");

//   useEffect(() => {
//     if (isFirstLogin && location.pathname !== "/security-setup") {
//       navigate("/security-setup", { replace: true });
//     }
//     prevPathnameRef.current = location.pathname;
//   }, [isFirstLogin, location.pathname, navigate]);

//   useEffect(() => {
//     if (!isFirstLogin) return;

//     const push = () => window.history.pushState(null, "", window.location.href);
//     push();

//     const onPop = () => {
//       if (prevPathnameRef.current === "/security-setup") {
//         push();
//       } else {
//         prevPathnameRef.current = location.pathname;
//       }
//     };

//     window.addEventListener("popstate", onPop);
//     return () => window.removeEventListener("popstate", onPop);
//   }, [isFirstLogin, location.pathname]);
// };

// ──────────────────────────────────────────────────────────────────────
// ROUTE GUARDS
// ──────────────────────────────────────────────────────────────────────
const getIsFirstLogin = () => localStorage.getItem("isFirstLogin") === "true";

const PublicRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const first = getIsFirstLogin();

  if (token && !first) return <Navigate to="/dashboard" replace />;
  // if (token && first) return <Navigate to="/security-setup" replace />;
  return <>{children}</>;
};

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const first = getIsFirstLogin();

  if (!token) return <Navigate to="/login" replace />;
  // if (first) return <Navigate to="/security-setup" replace />;
  return <>{children}</>;
};

const SecuritySetupRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const first = getIsFirstLogin();

  if (!token || !first) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

// --- SECURITY LOCK COMPONENT (inside Provider) ---
// const SecurityLock = () => {
//   useSecurityLock();
//   return null;
// };

// --- PERMISSION SOCKET COMPONENT (inside Provider) ---
const PermissionSocket = () => {
  usePermissionSocket();
  return null;
};

// const PermissionRoute = ({ children, resource }) => {
//   const { canRead } = usePermissions(resource);

//   // console.log("red permio", canRead)
//   // Dashboard is always allowed
//   if (resource === "dashboard") return children;
//   return canRead ? children : <Navigate to="/dashboard" replace />;
// };
// const PermissionRoute = ({ children, resource }) => {
//   const { canRead, isLoading } = usePermissions(resource);

//   if (isLoading) {
//     return (
//       <div className="flex items-center justify-center min-h-screen">
//         <Loader2 className="h-8 w-8 animate-spin text-primary" />
//       </div>
//     );
//   }

//   if (resource === "dashboard") return children;
//   return canRead ? children : <Navigate to="/dashboard" replace />;
// };

// const PermissionRoute = ({ children, resource }) => {
//   const { canRead, isLoading } = usePermissions(resource);

//   if (isLoading) {
//     return (
//       <div className="flex items-center justify-center min-h-screen">
//         <Loader2 className="h-8 w-8 animate-spin text-primary" />
//       </div>
//     );
//   }

//   // Dashboard is special — if user has NO dashboard access, send them to first allowed page
//   if (resource === "dashboard" && !canRead) {
//     return <Navigate to="/profile" replace />; // or "/no-access" page
//   }

//   return canRead ? children : <Navigate to="/dashboard" replace />;
// };

const PermissionRoute = ({ children, resource }) => {
  const { canRead, isLoading } = usePermissions(resource);

  // Do not redirect until permissions are 100% loaded
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Now safe to check dashboard permission
  if (resource === "dashboard" && !canRead) {
    return <Navigate to="/my-teams" replace />;
  }

  return canRead ? children : <Navigate to="/dashboard" replace />;
};

// --- MAIN APP WITH PROVIDERS ---
function App() {
  return (
    <Provider store={store}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <TooltipProvider>
          <Router>
            {/* <SecurityLock /> */}
            <PermissionSocket />
            <div className="App">
              <Routes>
                {/* Public */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <PublicRoute>
                      <ForgotPasswordPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/security-question-login"
                  element={
                    <PublicRoute>
                      <SecurityQuestionLoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/reset-password/:token"
                  element={
                    <PublicRoute>
                      <ResetPasswordPage />
                    </PublicRoute>
                  }
                />

                {/* Security Setup */}
                {/* <Route
                  path="/security-setup"
                  element={
                    <SecuritySetupRoute>
                      <SecuritySetupPage />
                    </SecuritySetupRoute>
                  }
                /> */}

                {/* Protected */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route
                    path="dashboard"
                    element={
                      <PermissionRoute resource="dashboard">
                        <Dashboard />
                      </PermissionRoute>
                    }
                  />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route
                    path="employee"
                    element={
                      <PermissionRoute resource="employee">
                        <EmployeePage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="my-teams"
                    element={
                      // <PermissionRoute resource="employee">
                      <MyTeamPage />
                      // </PermissionRoute>
                    }
                  />
                     <Route
                    path="/my-attendance"
                    element={
                      <PermissionRoute resource="break_time_log">
                        <AttendancePage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="general-log"
                    element={
                      <PermissionRoute resource="activity_logs">
                        <GeneralLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="employee-log"
                    element={
                      <PermissionRoute resource="employee_log">
                        <EmployeeLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="permissions-log"
                    element={
                      <PermissionRoute resource="permission_log">
                        <PermissionsLogPage />
                      </PermissionRoute>
                    }
                  />

                  <Route
                    path="break-log"
                    element={
                      <PermissionRoute resource="break_log">
                        <BreakLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="role"
                    element={
                      <PermissionRoute resource="role">
                        <RolePage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="department"
                    element={
                      <PermissionRoute resource="department">
                        <DepartmentPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="company"
                    element={
                      <PermissionRoute resource="company">
                        <CompanyPage />
                      </PermissionRoute>
                    }
                  />
                  {/* <Route
                    path="security-question"
                    element={
                      <PermissionRoute resource="password_reset_questions">
                        <SecurityEmployeeQuestionPage />
                      </PermissionRoute>
                    }
                  /> */}
                  <Route
                    path="category"
                    element={
                      <PermissionRoute resource="category">
                        <CategoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="subcategory"
                    element={
                      <PermissionRoute resource="sub_category">
                        <SubcategoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="vendor"
                    element={
                      <PermissionRoute resource="vendor">
                        <VendorPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="inventory"
                    element={
                      <PermissionRoute resource="inventory">
                        <InventoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="inventory-log"
                    element={
                      <PermissionRoute resource="inventory_log">
                        <InventoryLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stock"
                    element={
                      <PermissionRoute resource="stock">
                        <StockPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="canteen/menu"
                    element={
                      <PermissionRoute resource="menu">
                        <CanteenPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="raise-ticket"
                    element={
                      <PermissionRoute resource="ticket">
                        <RaiseTicketPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="request-issuance"
                    element={
                      <PermissionRoute resource="request">
                        <RequestIssuancePage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={<NotificationsPage />}
                  />
                  <Route
                    path="/asset-assign"
                    element={
                      <PermissionRoute resource="asset_assign">
                        <AssetAssignPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/suggestion"
                    element={
                      <PermissionRoute resource="suggestions">
                        <SuggestionPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/break-time-log"
                    element={
                      <PermissionRoute resource="break_time_log">
                        <BreakTimeLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/request-logs"
                    element={
                      <PermissionRoute resource="request_log">
                        <RequestLogsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/escalation-history-log"
                    element={
                      <PermissionRoute resource="escalation_history">
                        <EscalationHistoryLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/asset-assign-logs"
                    element={
                      <PermissionRoute resource="asset_assign_log">
                        <AssetAssignLogsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/ticket-logs"
                    element={
                      <PermissionRoute resource="ticket_log">
                        <TicketLogsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/canteen-logs"
                    element={
                      <PermissionRoute resource="canteen_log">
                        <CanteenLogsPage />
                      </PermissionRoute>
                    }
                  />

                  <Route
                    path="/update-wallet"
                    element={
                      <PermissionRoute resource="employee_wallet_history">
                        <UpdateWalletPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/order-list"
                    element={
                      <PermissionRoute resource="canteen_order_log">
                        <OrderListPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/customer-profile"
                    element={
                      <PermissionRoute resource="customer_profile">
                        <CustomerProfilePage />
                      </PermissionRoute>
                    }
                  />
                  <Route path="/settings" element={<SettingsPage />} />

                  <Route
                    path="/feedback"
                    element={
                      <PermissionRoute resource="feedback">
                        <FeedbackPage />
                      </PermissionRoute>
                    }
                  />
                  {/* <Route
                    path="/reports"
                    element={
                      <PermissionRoute resource="reports">
                        <ReportPage />
                      </PermissionRoute>
                    }
                  /> */}

                  <Route
                    path="/reports"
                    element={
                      <PermissionRoute resource="reports">
                        <ReportLayout />
                      </PermissionRoute>
                    }
                  >
                    <Route
                      index
                      element={
                        <Navigate to="/reports/inventory-summary" replace />
                      }
                    />

                    <Route
                      path="inventory-summary"
                      element={<InventorySummaryPage />}
                    />
                    <Route
                      path="stock-movement"
                      element={<StockMovementPage />}
                    />
                    <Route path="low-stock" element={<LowStockAlertPage />} />
                    <Route
                      path="expired-items"
                      element={<ExpiredItemsPage />}
                    />
                    <Route
                      path="consumption-trend"
                      element={<ConsumptionTrendPage />}
                    />
                    <Route
                      path="department-consumption"
                      element={<DepartmentConsumptionPage />}
                    />
                    <Route
                      path="purchase-history"
                      element={<PurchaseHistoryPage />}
                    />
                    <Route
                      path="price-variation"
                      element={<PriceVariationPage />}
                    />
                    <Route path="asset-master" element={<AssetMasterPage />} />
                    {/* <Route
                      path="warranty-expiry"
                      element={<WarrantyExpiryPage />}
                    />
                    <Route
                      path="asset-allocation"
                      element={<AssetAllocationPage />}
                    />
                    <Route path="idle-assets" element={<IdleAssetsPage />} />
                    <Route
                      path="asset-lifecycle-cost"
                      element={<AssetLifecycleCostPage />}
                    /> */}
                    <Route
                      path="break-summary"
                      element={<BreakSummaryPage />}
                    />
                    <Route
                      path="break-duration"
                      element={<BreakDurationPage />}
                    />
                    <Route
                      path="irregular-breaks"
                      element={<IrregularBreaksPage />}
                    />
                    <Route
                      path="time-deviation"
                      element={<TimeDeviationPage />}
                    />
                    <Route
                      path="tickets-overview"
                      element={<TicketsOverviewPage />}
                    />
                    <Route
                      path="tat-compliance"
                      element={<TatCompliancePage />}
                    />
                    <Route path="escalations" element={<EscalationsPage />} />
                    <Route
                      path="department-tickets"
                      element={<DepartmentTicketsPage />}
                    />
                    <Route
                      path="recurring-issues"
                      element={<RecurringIssuesPage />}
                    />
                    <Route
                      path="handler-productivity"
                      element={<HandlerProductivityPage />}
                    />
                    <Route
                      path="canteen-orders"
                      element={<CanteenOrdersPage />}
                    />
                    <Route
                      path="popular-items"
                      element={<PopularItemsPage />}
                    />
                    <Route
                      path="canteen-finance"
                      element={<CanteenFinancePage />}
                    />
                    <Route
                      path="ingredient-stock"
                      element={<IngredientStockPage />}
                    />
                  </Route>
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>

              <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
            </div>
          </Router>
        </TooltipProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
